import React, { useState, useMemo, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { 
  Box, 
  IconButton, 
  Tooltip, 
  TextField
} from '@mui/material';

// Generic DataTable Component
const DataTable = ({ 
  data,
  columns,
  searchTerm: externalSearchTerm,
  onSearchChange: externalOnSearchChange,
  showSearchOnly = false,
  searchPlaceholder = "Search...",
  enableRowSelection = true,
  enableSorting = false,
  enableGlobalFilter = false,
  enableColumnFilters = false,
  enablePagination = true,
  enableColumnActions = false,
  enableColumnDragging = false,
  enableColumnResizing = false,
  enableDensityToggle = false,
  enableFullScreenToggle = false,
  enableHiding = false,
  enableTopToolbar = false,
  enableBottomToolbar = true,
  pageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  showFirstButton = true,
  showLastButton = true,
  manualPagination = false, // Server-side pagination
  rowCount, // Total row count for server-side pagination
  onPaginationChange, // Pagination change handler
  pagination: externalPagination, // External pagination state { pageIndex, pageSize }
  customTableProps = {},
  customPaginationProps = {},
  customContainerProps = {},
  customTopToolbarProps = {},
  customBodyProps = {},
  searchFunction = null, // Custom search function
  onRowClick = null, // Row click handler
  loading = false, // Loading state
  emptyMessage = "No data available", // Empty state message
  height = "auto", // Table height
  maxHeight = null, // Max height for scrolling
  stickyHeader = false, // Sticky header
  dense = false, // Dense rows
  hover = true, // Row hover effect
  striped = true, // Alternating row colors
  border = true, // Table border
  shadow = true, // Box shadow
  borderRadius = "8px", // Border radius
  customStyles = {}, // Additional custom styles
  getRowStyle: getRowStyleProp, // function(row) => style object
  getRowDisabled, // function(row) => boolean
  showHorizontalScroll = false, // Show horizontal scrollbar (only for Projects and Tasks list)
}) => {
  const { mode } = useThemeMode();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  // Use external search term if provided, otherwise use internal state
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = externalOnSearchChange || setInternalSearchTerm;

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    if (searchFunction && typeof searchFunction === 'function') {
      return searchFunction(data, searchTerm);
    }
    
    // Default search implementation
    return data.filter(item => 
      Object.values(item).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm, searchFunction]);

  // Memoize the search handler
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);

  // Material React Table hook
  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    enableRowSelection,
    enableSorting,
    enableGlobalFilter,
    enableColumnFilters,
    enablePagination,
    enableColumnActions,
    enableColumnDragging,
    enableColumnResizing,
    enableDensityToggle,
    enableFullScreenToggle,
    enableHiding,
    enableTopToolbar,
    enableBottomToolbar,
    // CRITICAL: Disable virtualization which causes height issues
    enableRowVirtualization: false,
    enableColumnVirtualization: false,
    // Set layout mode to prevent height calculation issues
    layoutMode: 'semantic',
    // Server-side pagination
    manualPagination,
    ...(manualPagination && rowCount !== undefined && { rowCount }),
    ...(manualPagination && onPaginationChange && {
      onPaginationChange,
    }),
    ...(manualPagination && externalPagination && {
      state: {
        pagination: externalPagination,
      },
    }),
    initialState: {
      pagination: externalPagination || {
        pageSize,
        pageIndex: 0,
      },
    },
    muiTableProps: {
      sx: {
        backgroundColor: mode === 'dark' ? '#000000' : '#fff',
        width: '100%',
        '& .MuiTableHead-root': {
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
          ...(stickyHeader && {
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }),
        },
        '& .MuiTableCell-head': {
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontWeight: '600',
          fontSize: dense ? '12px' : '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          borderBottom: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
          padding: dense ? '8px 12px' : '16px 12px',
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
        },
        '& .MuiTableCell-body': {
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#eee'}`,
          padding: dense ? '8px 12px' : '16px 12px',
          backgroundColor: mode === 'dark' ? '#000000' : '#ffffff',
          ...(onRowClick && {
            cursor: 'pointer',
          }),
        },
        '& .MuiTableRow-root': {
          borderRadius: '0 !important', // Remove any rounded corners
          '&:last-child': {
            borderRadius: '0 !important', // Specifically target last row
            '& .MuiTableCell-root': {
              borderRadius: '0 !important', // Remove corners from last row cells
            }
          },
          ...(hover && {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa'
            },
          }),
          ...(striped && {
            '&:nth-of-type(even)': {
              backgroundColor: mode === 'dark' ? '#0a0a0a' : '#fafafa'
            },
            '&:nth-of-type(even):hover': {
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f0f0f0'
            }
          }),
        },
        // Ensure table cells don't have rounded corners
        '& .MuiTableCell-root': {
          borderRadius: '0 !important',
        },
        // Specifically target last row cells
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderRadius: '0 !important',
        },
        ...customTableProps.sx,
      },
      ...customTableProps,
    },
    muiTablePaginationProps: {
      rowsPerPageOptions: pageSizeOptions,
      showFirstButton,
      showLastButton,
      sx: {
        color: mode === 'dark' ? '#fff' : '#333',
        backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
        borderTop: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
        padding: '12px 24px',
        fontFamily: 'Urbanist, Arial, sans-serif',
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          color: mode === 'dark' ? '#bbb' : '#555',
          fontSize: '14px',
        },
        '& .MuiSelect-select': {
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
          color: mode === 'dark' ? '#fff' : '#333',
        },
        '& .MuiTablePagination-actions': {
          '& .MuiIconButton-root': {
            color: mode === 'dark' ? '#fff' : '#333',
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
            border: `1px solid ${mode === 'dark' ? '#333' : '#ccc'}`,
            borderRadius: '4px',
            margin: '0 4px',
            width: '32px',
            height: '32px',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#eee',
            },
            '&.Mui-disabled': {
              color: mode === 'dark' ? '#555' : '#aaa',
              backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f0f0f0',
              borderColor: mode === 'dark' ? '#222' : '#e0e0e0'
            }
          }
        },
        ...customPaginationProps.sx,
      },
      ...customPaginationProps,
    },
    muiTableContainerProps: {
      sx: {
        width: '100%',
        height,
        maxHeight,
        // Conditional horizontal scrolling with themed scrollbar
        overflowX: showHorizontalScroll ? 'auto' : 'hidden',
        overflowY: 'auto',
        ...(showHorizontalScroll && {
          paddingBottom: '4px', // Add space to make scrollbar more visible
          // Webkit scrollbar styling (Chrome, Safari, Opera) - More visible
          '&::-webkit-scrollbar': {
            height: '14px',
            width: '14px',
          },
          '&::-webkit-scrollbar-track': {
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: '7px',
            border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            margin: '2px 0',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? 'rgba(38, 195, 192, 0.7)' : 'rgba(38, 195, 192, 0.75)',
            borderRadius: '7px',
            border: mode === 'dark' ? '2px solid rgba(38, 195, 192, 0.3)' : '2px solid rgba(38, 195, 192, 0.2)',
            boxShadow: mode === 'dark' 
              ? '0 2px 4px rgba(38, 195, 192, 0.3)' 
              : '0 2px 4px rgba(38, 195, 192, 0.2)',
            minHeight: '30px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: mode === 'dark' ? 'rgba(38, 195, 192, 0.9)' : 'rgba(38, 195, 192, 0.95)',
            boxShadow: mode === 'dark' 
              ? '0 2px 6px rgba(38, 195, 192, 0.5)' 
              : '0 2px 6px rgba(38, 195, 192, 0.4)',
          },
          '&::-webkit-scrollbar-corner': {
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
          },
          // Firefox scrollbar styling - More visible
          scrollbarWidth: 'auto',
          scrollbarColor: mode === 'dark' 
            ? 'rgba(38, 195, 192, 0.7) rgba(255, 255, 255, 0.1)' 
            : 'rgba(38, 195, 192, 0.75) rgba(0, 0, 0, 0.08)',
        }),
        ...(!showHorizontalScroll && {
          // Hide scrollbars for other pages
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
        }),
        ...(border && {
          border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
          borderRadius,
        }),
        backgroundColor: mode === 'dark' ? '#000000' : '#ffffff',
        ...(shadow && {
          boxShadow: 'none', // Remove shadow completely
        }),
        '& .MuiTable-root': {
          marginTop: 0,
          overflowX: 'auto',
        },
        ...customContainerProps.sx,
      },
      ...customContainerProps,
    },
    muiTopToolbarProps: {
      sx: {
        display: enableTopToolbar ? 'flex' : 'none',
        ...customTopToolbarProps.sx,
      },
      ...customTopToolbarProps,
    },
    muiTableBodyProps: {
      sx: {
        '& .MuiTableBody-root': {
          height: 'auto !important',
          overflow: 'visible !important'
        },
        ...customBodyProps.sx,
      },
      ...customBodyProps,
    },
    // Row-level styling and disabling
    muiTableBodyRowProps: ({ row }) => {
      const original = row.original;
      const rowDisabled = typeof getRowDisabled === 'function' ? !!getRowDisabled(original) : false;
      const rowStyle = typeof getRowStyleProp === 'function' ? getRowStyleProp(original) : undefined;
      return {
        onClick: onRowClick && !rowDisabled ? () => onRowClick(original) : undefined,
        sx: {
          cursor: onRowClick && !rowDisabled ? 'pointer' : 'default',
          pointerEvents: rowDisabled ? 'none' : 'auto',
          ...(rowStyle || {}),
        },
      };
    },
    // Loading state
    ...(loading && {
      state: { isLoading: true },
    }),
    // Empty state
    ...(filteredData.length === 0 && !loading && {
      renderDetailPanel: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            color: mode === 'dark' ? '#bbb' : '#666',
            fontSize: '14px',
            fontFamily: 'Urbanist, Arial, sans-serif',
          }}
        >
          {emptyMessage}
        </Box>
      ),
    }),
  });

  // If showSearchOnly is true, only render the search input
  if (showSearchOnly) {
    return (
      <TextField
        placeholder={searchPlaceholder}
        size="small"
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
            '& fieldset': {
              borderColor: mode === 'dark' ? '#444' : '#ddd'
            },
            '&:hover fieldset': {
              borderColor: '#26C3C0'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#26C3C0'
            }
          },
          '& .MuiInputBase-input': {
            color: mode === 'dark' ? '#fff' : '#333'
          }
        }}
      />
    );
  }

  return (
    <div style={customStyles}>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default DataTable; 